// (!) THIS FILE SHOULD NOT BE EXPORTED OR USED ANYWHERE BUT IN THE BASKETSTORE (!)
// (!) IT ONLY EXISTS TO DECLUTTER THE BASKETSTORE (!)
import { Product, BasketEntry } from "@daalder/types";
import {
    AddCombinationUpsellToBasketData,
    AddExpiviProductToBasketData,
    AddProductToBasketData,
    AddWizardToBasketData,
} from "../types/BasketMutationTypes";
import { defineStore } from "pinia";
import { useBasketStore } from "../stores/BasketStore";
import { useGA4 } from "./GA4";
import ApiService from "./ApiService";

// Data & such
let basketStore: Omit<ReturnType<typeof useBasketStore>, keyof ReturnType<typeof defineStore>> = null;
const apiService = new ApiService();
const { trackGAAddToCart, trackGARemoveFromCart } = useGA4();

/**
 * @description Do not use this composable directly! Use the BasketStore instead.
 */
export function useBasketEntryMutations() {
    /**
     * @description This method is a workaround for a timing issue that can occur with Pinia stores in Composables.
     */
    function getBasketStore() {
        if (basketStore === null) {
            basketStore = useBasketStore();
        }

        return basketStore;
    }

    function makeProductPartsPayloadFromBasketEntryCombinationUpsell(basketEntry: BasketEntry): {
        product_id: number;
        amount: number;
    }[] {
        return basketEntry.upsell?.products?.map((product) => {
            return {
                product_id: product.id,
                amount: product.amount,
            };
        });
    }

    function makeProductPartsPayloadFromBasketEntryWizard(basketEntry: BasketEntry): {
        id: string;
        product_id: number;
        amount: number;
    }[] {
        return basketEntry.product_parts?.map((product) => {
            return {
                id: product.part_id,
                product_id: product.id,
                amount: product.amount,
            };
        });
    }

    function makeEntriesPayloadFromAddProductToBasketData(
        addProductToBasketData: (AddProductToBasketData | AddCombinationUpsellToBasketData | AddWizardToBasketData)[],
    ) {
        return addProductToBasketData.map((productData) => {
            // Prepare the entry data
            let entryData: {
                product_id: number;
                amount: number;
                options: { [key: string]: any };
                product_parts?: {
                    product_id: number;
                    amount: number;
                }[];
                upsell_id?: number;
                wizard_id?: number;
            } = {
                product_id: productData.product.id,
                amount: productData.amount,
                options: [],
            };

            // Iterate over the productoptions
            Object.keys(productData.chosen_options ?? {}).forEach((optionId) => {
                let optionValue = productData.chosen_options[optionId];

                // Checkbox productoptions can have multiple checkboxes and therefore multiple values. If the
                // productoption value is an array, this is the case. If so, potentially multiple options should be
                // pushed to the API for this option id.
                if (Array.isArray(optionValue)) {
                    optionValue.forEach((optionValueInArray) => {
                        entryData.options.push({
                            option_id: optionId,
                            option_value: optionValueInArray,
                        });
                    });
                } else if (optionValue) {
                    // Else, if the value is non-nullish, one option should be pushed to the API for this option id.
                    entryData.options.push({
                        option_id: optionId,
                        option_value: optionValue,
                    });
                }
            });

            if (Object.hasOwn(productData, "product_parts")) {
                entryData.product_parts = (productData as AddCombinationUpsellToBasketData).product_parts.map(
                    (productPart) => {
                        return {
                            product_id: productPart.product.id,
                            amount: productPart.amount,
                        };
                    },
                );
            }

            if (Object.hasOwn(productData, "upsell_id")) {
                entryData.upsell_id = (productData as AddCombinationUpsellToBasketData).upsell_id;
            }

            if (Object.hasOwn(productData, "wizard_id")) {
                entryData.wizard_id = (productData as AddWizardToBasketData).wizard_id;
            }

            // Return the formatted entry data
            return entryData;
        });
    }

    async function addEntry(
        productsData: AddProductToBasketData,
        replaceEntryAmount: boolean = true,
        showConfirmationModal: boolean = true,
        GA4ListName: string = null,
    ) {
        // Wrap the arguments and pass them on.
        return addEntries([productsData], replaceEntryAmount, showConfirmationModal, GA4ListName);
    }

    async function addEntries(
        productsData: AddProductToBasketData[],
        replaceEntryAmount: boolean = true,
        showConfirmationModal: boolean = true,
        GA4ListName: string = null,
    ) {
        if (showConfirmationModal) {
            // Configure the confirmation modal
            getBasketStore().isConfirmationModalLoadingState = true;
            getBasketStore().justAddedProductsData = productsData;
            getBasketStore().isConfirmationModalOpen = true;
        }

        // Prepare the URL and map the payload data
        let url = replaceEntryAmount ? "v2/basket/entries" : "v2/basket/entries/add";
        let data = {
            entries: makeEntriesPayloadFromAddProductToBasketData(productsData),
        };

        // Add the entries to the Basket
        return apiService
            .post(`${url}?includes=${getBasketStore().apiIncludes.join(",")}`, data)
            .then((response: any) => {
                // Update the local Basket & confirmation modal state
                getBasketStore().basket = response.data;

                if (showConfirmationModal) {
                    getBasketStore().isConfirmationModalLoadingState = false;
                }

                // Track GA4 event
                trackGAAddToCart(
                    productsData.map((productData) => ({ amount: productData.amount, product: productData.product })),
                    GA4ListName,
                );

                return response;
            })
            .catch((error: any) => {
                throw error;
            });
    }

    async function removeEntry(basketEntry: BasketEntry, gaListName: string = null): Promise<any> {
        getBasketStore().isCurrentlyMutating = true;

        return apiService
            .delete(`v2/basket/entries/${basketEntry.id}?includes=${getBasketStore().apiIncludes.join(",")}`)
            .then((response: any) => {
                // GA4
                trackGARemoveFromCart(
                    [
                        {
                            amount: basketEntry.amount,
                            product: basketEntry.product,
                        },
                    ],
                    gaListName,
                );

                getBasketStore().basket = response.data;

                return response;
            })
            .catch((error: any) => {
                throw error;
            })
            .finally(() => {
                getBasketStore().isCurrentlyMutating = false;
            });
    }

    async function updateEntry(
        basketEntry: BasketEntry,
        newAmount: number,
        productParts: { product_id: number; amount: number }[] | null = null,
        gaListName: string = null,
        options: { option_id: number; option_value: number }[] | null = null,
        expivi?: { bundle_uuid: string; configured_products: any[] } | null,
    ): Promise<any> {
        getBasketStore().isCurrentlyMutating = true;

        let data: {
            product_id: number;
            amount: number;
            product_parts?: any;
            options?: { option_id: number; option_value: number }[];
        } = {
            product_id: basketEntry.product_id,
            amount: newAmount,
        };

        if (productParts) {
            data["product_parts"] = productParts;
        }
        if (options) {
            data["options"] = options;
        }

        if (expivi) {
            data["expivi"] = expivi;
        }

        return apiService
            .put(`v2/basket/entries/${basketEntry.id}?includes=${getBasketStore().apiIncludes.join(",")}`, data)
            .then((response: any) => {
                const updatedBasketEntry = response.data.entries.find((entry: BasketEntry) => {
                    return entry.id === basketEntry.id;
                });

                if (updatedBasketEntry.amount < basketEntry.amount) {
                    // GA4
                    trackGARemoveFromCart(
                        [
                            {
                                amount: basketEntry.amount,
                                product: basketEntry.product,
                            },
                        ],
                        gaListName,
                    );
                }

                if (updatedBasketEntry.amount > basketEntry.amount) {
                    // GA4
                    trackGAAddToCart(
                        [
                            {
                                amount: basketEntry.amount,
                                product: basketEntry.product,
                            },
                        ],
                        gaListName,
                    );
                }

                getBasketStore().basket = response.data;

                return response;
            })
            .catch((error: any) => {
                throw error;
            })
            .finally(() => {
                getBasketStore().isCurrentlyMutating = false;
            });
    }

    // WIZARD PARTS
    async function addWizardEntry(wizardData: AddWizardToBasketData, showConfirmationModal: boolean = true) {
        if (showConfirmationModal) {
            // Configure the confirmation modal
            getBasketStore().isConfirmationModalLoadingState = true;
            getBasketStore().justAddedProductsData = [wizardData];
            getBasketStore().isConfirmationModalOpen = true;
        }

        // Prepare the URL and map the payload data
        let data = makeEntriesPayloadFromAddProductToBasketData([wizardData])[0];

        // Add the entries to the Basket
        return apiService
            .post(`v2/basket/add-wizard?includes=${getBasketStore().apiIncludes.join(",")}`, data)
            .then((response: any) => {
                // Update the local Basket & confirmation modal state
                getBasketStore().basket = response.data;

                if (showConfirmationModal) {
                    getBasketStore().isConfirmationModalLoadingState = false;
                }

                // GA4
                trackGAAddToCart([
                    { amount: wizardData.amount, product: wizardData.product },
                    ...(wizardData.product_parts ?? []).map((part) => {
                        return { amount: part.amount, product: part.product };
                    }),
                ]);

                return response;
            })
            .catch((error: any) => {
                throw error;
            });
    }

    async function addWizardPart() {
        // TODO
    }

    async function removeWizardPart(basketEntry: BasketEntry, wizardPart: Product) {
        let productParts = makeProductPartsPayloadFromBasketEntryWizard(basketEntry)?.filter((productPart) => {
            return productPart.product_id !== wizardPart.id;
        });

        return await updateBasketEntryParts(basketEntry, productParts);
    }

    async function updateWizardPart(basketEntry: BasketEntry, wizardPart: Product, amount: number) {
        // Update the local state first, so that if this request gets cancelled by a subsequent request, the data
        // isn't lost.
        let localBasketEntry: BasketEntry = getBasketStore().basket.entries.find((entry: BasketEntry) => {
            return entry.id == basketEntry.id;
        });

        let localWizardPart: Product = localBasketEntry.product_parts?.find((productPart: Product) => {
            return productPart.id == wizardPart.id;
        });

        localWizardPart.amount = amount;

        // Prepare product parts payload
        let productParts = makeProductPartsPayloadFromBasketEntryWizard(localBasketEntry);

        return await updateBasketEntryParts(basketEntry, productParts);
    }

    // COMBINATION UPSELL PARTS
    async function addCombinationUpsellEntry(
        combinationUpsellData: AddCombinationUpsellToBasketData,
        showConfirmationModal: boolean = true,
    ) {
        if (showConfirmationModal) {
            // Configure the confirmation modal
            getBasketStore().isConfirmationModalLoadingState = true;
            getBasketStore().justAddedProductsData = [combinationUpsellData];
            getBasketStore().isConfirmationModalOpen = true;
        }

        // Prepare the URL and map the payload data
        let data = makeEntriesPayloadFromAddProductToBasketData([combinationUpsellData])[0];

        // Add the entries to the Basket
        return apiService
            .post(`v2/basket/add-upsell?includes=${getBasketStore().apiIncludes.join(",")}`, data)
            .then((response: any) => {
                // Update the local Basket & confirmation modal state
                getBasketStore().basket = response.data;

                if (showConfirmationModal) {
                    getBasketStore().isConfirmationModalLoadingState = false;
                }

                // GA4
                trackGAAddToCart([
                    { amount: combinationUpsellData.amount, product: combinationUpsellData.product },
                    ...(combinationUpsellData.product_parts ?? []).map((part) => {
                        return { amount: part.amount, product: part.product };
                    }),
                ]);

                return response;
            })
            .catch((error: any) => {
                throw error;
            });
    }

    async function addCombinationUpsellPart() {
        // TODO
    }

    async function addExpiviEntry(
        expiviPayloadData: AddExpiviProductToBasketData,
        showConfirmationModal: boolean = true,
    ) {
        if (showConfirmationModal) {
            // Configure the confirmation modal
            getBasketStore().isConfirmationModalLoadingState = true;
            getBasketStore().justAddedProductsData = [expiviPayloadData];
            getBasketStore().isConfirmationModalOpen = true;
        }

        const { bundle_uuid, configured_products } = expiviPayloadData.expivi;

        const expiviPayload = {
            product_id: expiviPayloadData.product.id,
            amount: expiviPayloadData.amount,
            bundle_uuid,
            configured_products,
        };

        return apiService
            .post(`v2/basket/expivi-entries`, expiviPayload)
            .then((response: any) => {
                // Update the local Basket & confirmation modal state
                getBasketStore().basket = response.data;

                if (showConfirmationModal) {
                    getBasketStore().isConfirmationModalLoadingState = false;
                }
            })
            .catch((error: any) => {
                throw error;
            });
    }

    async function updateExpiviEntry(
        basketEntryId: number,
        expiviPayloadData: AddExpiviProductToBasketData,
        showConfirmationModal: boolean = true,
    ) {
        if (showConfirmationModal) {
            // Configure the confirmation modal
            getBasketStore().isConfirmationModalLoadingState = true;
            getBasketStore().justAddedProductsData = [expiviPayloadData];
            getBasketStore().isConfirmationModalOpen = true;
        }

        const basketEntry = getBasketStore().basket.entries.find((entry: BasketEntry) => {
            return entry.id == basketEntryId;
        });

        return updateEntry(basketEntry, expiviPayloadData.amount, null, null, null, expiviPayloadData.expivi).then(
            (response: any) => {
                if (showConfirmationModal) {
                    getBasketStore().isConfirmationModalLoadingState = false;
                }
            },
        );
    }

    async function removeCombinationUpsellPart(basketEntry: BasketEntry, combinationUpsellPartProduct: Product) {
        let productParts = makeProductPartsPayloadFromBasketEntryCombinationUpsell(basketEntry)?.filter(
            (productPart) => {
                return productPart.product_id !== combinationUpsellPartProduct.id;
            },
        );

        return await updateBasketEntryParts(basketEntry, productParts);
    }

    async function updateCombinationUpsellPart(
        basketEntry: BasketEntry,
        combinationUpsellPart: Product,
        amount: number,
    ): Promise<any> {
        // Update the local state first, so that if this request gets cancelled by a subsequent request, the data
        // isn't lost.
        let localBasketEntry: BasketEntry = getBasketStore().basket.entries.find((entry: BasketEntry) => {
            return entry.id == basketEntry.id;
        });

        let localCombinationUpsellPart: Product = localBasketEntry.upsell?.products?.find((productPart: Product) => {
            return productPart.id == combinationUpsellPart.id;
        });

        localCombinationUpsellPart.amount = amount;

        // Prepare product parts payload
        let productParts = makeProductPartsPayloadFromBasketEntryCombinationUpsell(localBasketEntry);

        return await updateBasketEntryParts(basketEntry, productParts);
    }

    async function updateBasketEntryParts(
        basketEntry: BasketEntry,
        productParts: {
            id?: string;
            product_id: number;
            amount: number;
        }[],
    ) {
        getBasketStore().isCurrentlyMutating = true;

        return apiService
            .put(`v2/basket/entries/${basketEntry.id}?includes=${getBasketStore().apiIncludes.join(",")}`, {
                product_id: basketEntry.product_id,
                amount: basketEntry.amount,
                product_parts: productParts,
            })
            .then((response: any) => {
                getBasketStore().basket = response.data;
                return response;
            })
            .catch((error: any) => {
                throw error;
            })
            .finally(() => {
                getBasketStore().isCurrentlyMutating = false;
            });
    }

    return {
        addEntry,
        addEntries,
        removeEntry,
        updateEntry,
        addWizardEntry,
        addWizardPart,
        removeWizardPart,
        updateWizardPart,
        addCombinationUpsellEntry,
        addCombinationUpsellPart,
        removeCombinationUpsellPart,
        updateCombinationUpsellPart,
        updateBasketEntryParts,
        addExpiviEntry,
        updateExpiviEntry,
    };
}
